<template>
  <div class="rating-overlay">
    <p class="rating-overlay__prompt">
      {{ question }}
    </p>
    <div class="rating-overlay__stars" role="radiogroup" :aria-label="t('player.feedback.ratingLabel')">
      <button
        v-for="star in 5"
        :key="star"
        type="button"
        role="radio"
        :aria-checked="star === selectedRating"
        class="rating-overlay__star"
        :class="{ 'rating-overlay__star--active': star <= selectedRating }"
        :aria-label="t('player.feedback.starLabel', { count: star })"
        @click="selectedRating = star"
      >
        <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28">
          <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
        </svg>
      </button>
    </div>
    <div v-if="enableComment" class="rating-overlay__comment">
      <textarea
        v-model="comment"
        class="rating-overlay__textarea"
        :placeholder="t('player.feedback.commentPlaceholder')"
        rows="2"
        :aria-label="t('player.feedback.commentLabel')"
      />
    </div>
    <button
      class="rating-overlay__submit"
      :class="{ 'rating-overlay__submit--submitted': submitted }"
      :disabled="selectedRating === 0 || submitted"
      :aria-label="submitted ? t('player.feedback.ratingSubmitted') : t('player.feedback.submitRating')"
      @click="handleSubmit"
    >
      {{ submitted ? t('player.feedback.submitted') : t('player.feedback.submit') }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, type PropType } from 'vue'
import { useVideoI18n } from '../../../i18n'

const { t } = useVideoI18n()

const props = defineProps({
  payload: {
    type: Object as PropType<Record<string, unknown>>,
    default: () => ({}),
  },
})

const emit = defineEmits<{
  submit: [data: { rating: number; comment: string }]
}>()

const question = computed(
  () => (props.payload.question as string) || 'How would you rate this video?',
)
const enableComment = computed(() => (props.payload.enable_comment as boolean) ?? false)
const selectedRating = ref(0)
const comment = ref('')
const submitted = ref(false)

function handleSubmit() {
  if (selectedRating.value === 0 || submitted.value) return
  submitted.value = true
  emit('submit', { rating: selectedRating.value, comment: comment.value })
}
</script>

<style scoped lang="scss">
.rating-overlay {
  display: flex;
  flex-direction: column;
  gap: 18px;
  align-items: center;
  text-align: center;
  padding-top: 16px;

  &__prompt {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.5;
    color: rgba(255, 255, 255, 0.85);
    letter-spacing: 0.01em;
    white-space: pre-line;
  }

  &__stars {
    display: flex;
    gap: 4px;
    padding: 6px 10px;
    background: rgba(255, 255, 255, 0.04);
    border-radius: 12px;
  }

  &__star {
    background: transparent;
    border: none;
    // 0.18 was ~1.6:1 on the card, under the 3:1 a control's icon needs; 0.45 is ~4.4:1.
    color: rgba(255, 255, 255, 0.45);
    cursor: pointer;
    padding: 4px;
    border-radius: 8px;
    display: flex;
    transition: color 180ms ease, transform 180ms ease, filter 180ms ease;

    svg {
      width: 32px;
      height: 32px;
    }

    &:hover {
      color: #fbbf24;
      transform: scale(1.15);
      filter: drop-shadow(0 0 8px rgba(251, 191, 36, 0.5));
    }

    &--active {
      color: #fbbf24;
      filter: drop-shadow(0 0 6px rgba(251, 191, 36, 0.35));
    }

    &:focus-visible {
      outline: 2px solid #fff;
      outline-offset: 2px;
    }
  }

  &__comment {
    width: 100%;
  }

  &__submit {
    align-self: center;
    padding: 10px 32px;
    background: var(--vp-primary-color, #007bff);
    color: var(--vp-primary-ink, #fff);
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.02em;
    border: none;
    border-radius: 10px;
    cursor: pointer;
    transition: transform 200ms ease, box-shadow 200ms ease, opacity 200ms ease;

    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow:
        0 4px 16px rgba(0, 0, 0, 0.3),
        0 1px 3px rgba(0, 0, 0, 0.2);
    }

    &:active:not(:disabled) {
      transform: translateY(0);
    }

    // A neutral surface rather than a faded primary: at 0.4 opacity the button all but vanished,
    // and the viewer could not tell there was a step left once a star was picked.
    &:disabled:not(.rating-overlay__submit--submitted) {
      background: rgba(255, 255, 255, 0.12);
      color: rgba(255, 255, 255, 0.7);
      cursor: not-allowed;
    }

    &--submitted {
      background: linear-gradient(135deg, #22c55e, #16a34a);
      color: #fff;
      opacity: 1 !important;
    }

    &:focus-visible {
      outline: 2px solid #fff;
      outline-offset: 2px;
    }
  }

  &__textarea {
    width: 100%;
    padding: 12px 14px;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 10px;
    color: #fff;
    font-size: 13px;
    line-height: 1.5;
    resize: vertical;
    font-family: inherit;
    transition: border-color 200ms ease, box-shadow 200ms ease, background 200ms ease;

    // 0.35 was ~3.2:1, under the 4.5:1 of text; 0.6 is ~6.7:1.
    &::placeholder {
      color: rgba(255, 255, 255, 0.6);
    }

    &:focus {
      outline: none;
      border-color: rgba(251, 191, 36, 0.35);
      background: rgba(255, 255, 255, 0.08);
      box-shadow: 0 0 0 3px rgba(251, 191, 36, 0.1);
    }

    &:focus-visible {
      outline: 2px solid #fff;
      outline-offset: 2px;
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .rating-overlay__star,
  .rating-overlay__submit,
  .rating-overlay__textarea {
    transition-duration: 1ms;
  }
}

// Light theme
[data-vp-theme='light'] .rating-overlay {
  &__prompt {
    color: rgba(0, 0, 0, 0.85);
  }

  &__stars {
    background: rgba(0, 0, 0, 0.04);
  }

  // This block outranks the base `--active` and `:hover` rules, so it restates them: without that
  // a picked star stayed grey, and choosing one looked like nothing happened.
  &__star {
    // 0.15 was ~1.4:1 on the card; 0.45 is ~3.3:1.
    color: rgba(0, 0, 0, 0.45);

    &:hover,
    &--active {
      color: #fbbf24;
      filter: drop-shadow(0 0 6px rgba(251, 191, 36, 0.35));
    }

    &:focus-visible {
      outline-color: #000000;
    }
  }

  &__textarea {
    background: rgba(0, 0, 0, 0.04);
    border-color: rgba(0, 0, 0, 0.12);
    color: #000000;

    // 0.35 was ~2.4:1; 0.55 is ~4.7:1.
    &::placeholder {
      color: rgba(0, 0, 0, 0.55);
    }

    &:focus {
      background: rgba(0, 0, 0, 0.06);
    }

    &:focus-visible {
      outline-color: #000000;
    }
  }

  &__submit {
    &:disabled:not(.rating-overlay__submit--submitted) {
      background: rgba(0, 0, 0, 0.08);
      color: rgba(0, 0, 0, 0.6);
    }

    &:focus-visible {
      outline-color: #000000;
    }
  }
}
</style>
